import { inject, Injectable } from '@angular/core';
import { filter, Observable, of, switchMap, take } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { APP_CONFIG } from '@application/config';
import { TOperationException, TPersonSettings, TOpeningHours, TOperationsDaysOfWeek, TPrintReceiptCount, TSpace, TSpaceType, TOData, TSpooler, TActivities, TCategory, TAverageTicket } from '../entities/settings.model';
import { AuthStore } from '@auth/data-access';
import { TActiveResourceById } from '../entities/settings.interface';
import { DateTime } from 'luxon';

@Injectable(
  { providedIn: 'root' }
)
export class GeneralService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;
  private readonly authStore = inject(AuthStore);

  public updatePersonSettings(data: Partial<TPersonSettings>): Observable<TPersonSettings> {
    return this.http.patch<TPersonSettings>(this.apiUrl.personService + `/PersonSettings`, data,
      { withCredentials: true }
    );
  }

  public getPersonSettings(personId?: string): Observable<TPersonSettings> {
    if (!personId) {
      personId = this.authStore?.getAuthData()?.TenantId
    }
    return this.http.get<TPersonSettings>(this.apiUrl.personService + `/PersonSettings?id=${personId}`,
      { withCredentials: true }
    );
  }

  public getOperationsExceptions(): Observable<TOperationException[]> {
    const year = DateTime.now().year;
    return this.http.get<TOperationException[]>(this.apiUrl.account_service + `/OperationException/GetOperationsExceptions?year=${year}`,
      { withCredentials: true }
    );
  }

  public updateOperationsExceptions(data: Partial<TOperationException>): Observable<TOperationException> {
    return this.http.patch<TOperationException>(this.apiUrl.account_service + `/OperationException`, data,
      { withCredentials: true });
  }

  public removeOperationsExceptions(data: Partial<TOperationException>): Observable<TOperationException[]> {
    return this.http.delete<TOperationException[]>(this.apiUrl.account_service + `/OperationException?id=${data.OperationExceptionId}`,
      { withCredentials: true }
    );
  }

  public addOperationsExceptions(data: Partial<TOperationException>): Observable<TOperationException> {
    if (!data.TenantId) data.TenantId = this.authStore.getAuthData()?.TenantId;
    return this.http.post<TOperationException>(this.apiUrl.account_service + `/OperationException`, data,
      { withCredentials: true }
    );
  }

  public getPrintReceiptCount(): Observable<TPrintReceiptCount> {
    return this.authStore.observableAuthData().pipe(
      filter((authData) => !!authData?.TenantId),
      take(1),
      switchMap((authData) =>
        this.http.get<TPrintReceiptCount>(
          `${this.apiUrl.receivable_service}/ReceivableSettings?id=${authData.TenantId}`,
          {
            withCredentials: true,
          }
        )
      )
    );
  }

  public updatePrintReceiptCount(payload: TPrintReceiptCount): Observable<TPrintReceiptCount> {
    return this.http.put<TPrintReceiptCount>(`${this.apiUrl.receivable_service}/ReceivableSettings`, payload, {
      withCredentials: true,
    });
  }
  public getOpeningHours(): Observable<TOpeningHours> {
    return this.http.get<TOpeningHours>(this.apiUrl.account_service + `/OperationPeriod/GetOperationsPeriods`, { withCredentials: true });
  }

  public updateOpeningHours(data: Partial<TOpeningHours>): Observable<TOpeningHours> {
    return this.http.put<TOpeningHours>(this.apiUrl.account_service + `/OperationPeriod`, data,
      { withCredentials: true });
  }

  public getSpaces(): Observable<{ value: TSpace[], '@odata.count': number }> {
    const url = `${this.apiUrl.account_service}/Odata/Spaces`;
    const params = new HttpParams()
      .set('$top', '5')
      .set('$select', 'Description,SpaceTypeDescription,IsIndoor,AddedOn,UpdatedOn,IsActive,SpaceId,SpaceTypeId')
      .set('$count', 'true');

    return this.http.get<{ value: TSpace[], '@odata.count': number }>(url, {
      params,
      withCredentials: true,
    });
  }

  public addActivities(data: Partial<TActivities>): Observable<TActivities> {
    return this.http.post<TActivities>(`${this.apiUrl.activity_service}/Activity`, data, { withCredentials: true });
  }

  public getActivityById(activityId: string): Observable<TActivities> {
    return this.http.get<TActivities>(`${this.apiUrl.activity_service}/Activity?id=${activityId}`, { withCredentials: true });
  }

  public removeActivities(ActivityId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.activity_service}/Activity?id=${ActivityId}`, { withCredentials: true });
  }

  public addSpace(data: Partial<TSpace>): Observable<TSpace> {
    return this.http.post<TSpace>(`${this.apiUrl.account_service}/Space`, data, { withCredentials: true });
  }

  public updateSpace(data: Partial<TSpace>): Observable<TSpace> {
    return this.http.patch<TSpace>(`${this.apiUrl.account_service}/Space`, data, { withCredentials: true });
  }

  public updateActivities(data: Partial<TActivities>): Observable<TActivities> {
    return this.http.put<TActivities>(`${this.apiUrl.activity_service}/Activity`, data, { withCredentials: true });
  }


  public removeSpace(spaceId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.account_service}/Space?id=${spaceId}`, { withCredentials: true });
  }

  public getSpaceTypes(): Observable<TSpaceType[]> {
    return this.http.get<TSpaceType[]>(`${this.apiUrl.account_service}/SpaceType/GetSpacesTypes`, { withCredentials: true });
  }

  public getSpoolers(tenantId: string): Observable<TSpooler[]> {
    return this.http.get<TSpooler[]>(`${this.apiUrl.spooler}/Device/List/${tenantId}`, {
      withCredentials: true
    });
  }

  public updateSpooler(data: TSpooler): Observable<void> {
    return this.http.put<void>(`${this.apiUrl.spooler}/Device`, data, {
      withCredentials: true
    });
  }

  public getPairingCode(): Observable<TSpooler> {
    return this.http.get<TSpooler>(`${this.apiUrl.spooler}/Device/Create`, {
      withCredentials: true
    });
  }

  public removeSpooler(spoolerId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.spooler}/Device/${spoolerId}`, {
      withCredentials: true,
      responseType: 'text' as any
    });
  }

  public validateOperationPeriods(operationPeriod: TOperationsDaysOfWeek[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl.booking_service}/BookingException/ValidateOperationsPeriods`, operationPeriod, {
      withCredentials: true,
    });
  }

  public cancelBookingByOperationPeriod(operationPeriod: TOperationsDaysOfWeek[], confirmation: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl.booking_service}/BookingException/CancelBookingsByOperationsPeriods`, operationPeriod, {
      params: {
        confirmation
      },
      withCredentials: true,
    });
  }

  public validateOperationException(day: string, isOpen: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl.booking_service}/BookingException/ValidateOperationException`, null, {
      params: {
        day,
        isOpen
      },
      withCredentials: true,
    });
  }

  public cancelBookingsByOperationException(day: string, confirmation: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl.booking_service}/BookingException/CancelBookingsByOperationException`, null, {
      params: {
        day,
        confirmation
      },
      withCredentials: true,
    });
  }

  public getActiveGroupsBySpaceId(spaceId: string): Observable<TActiveResourceById[]> {
    return this.http.get<TActiveResourceById[]>(`${this.apiUrl.account_service}/Space/GetActiveGroupsBySpaceId`, {
      params: { spaceId },
      withCredentials: true
    });
  }

  public getActiveBookingsBySpaceId(spaceId: string): Observable<TActiveResourceById[]> {
    return this.http.get<TActiveResourceById[]>(`${this.apiUrl.account_service}/Space/GetActiveBookingsBySpaceId`, {
      params: { spaceId },
      withCredentials: true
    });
  }

  public getActiveBookingsByActivityId(activityId: string): Observable<TActiveResourceById[]> {
    return this.http.get<TActiveResourceById[]>(`${this.apiUrl.activity_service}/Activity/GetActiveBookingsByActivityId`, {
      params: { activityId },
      withCredentials: true
    });
  }

  public getCategories(): Observable<TCategory[]> {
    return this.http.get<TCategory[]>(`${this.apiUrl.personService}/FinancialCategory/GetAllFinancialCategories`, {
      withCredentials: true
    });
  }

  public createCategory(category: Omit<TCategory, 'FinancialCategoryId'>): Observable<TCategory> {
    return this.http.post<TCategory>(`${this.apiUrl.personService}/FinancialCategory`, category, { withCredentials: true });
  }

  public updateCategory(category: Partial<TCategory>): Observable<TCategory> {
    return this.http.put<TCategory>(`${this.apiUrl.personService}/FinancialCategory`, category, { withCredentials: true });
  }

  public deleteCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/FinancialCategory`, {
      params: { id },
      withCredentials: true
    });
  }

  public getAverageTicketSettings(): Observable<TAverageTicket> {
    const authData = this.authStore.getAuthData();
    const tenantId = authData?.TenantId;
    if (!tenantId) {
      return of(null);
    }
    return this.http.get<TAverageTicket>(`${this.apiUrl.personService}/AverageTicketSettings?id=${tenantId}`, {
      withCredentials: true
    });
  }

  public updateAverageTicketSettings(averageTicket: TAverageTicket): Observable<TAverageTicket> {
    return this.http.put<TAverageTicket>(`${this.apiUrl.personService}/AverageTicketSettings`, averageTicket, {
      withCredentials: true
    });
  }
}
